import { ORGANISATIONS_TYP_LABELS } from 'src/utils/format-organisation-type';

import type { OrganisationsTyp } from 'src/types/nya/common';

/**
 * Avgör om organisationen är arbetsgivare, och därmed om arbetsgivardeklarationer
 * hör hemma i att göra-listan.
 *
 * Skatteverket exponerar inget register över arbetsgivarregistrerade
 * organisationer, så frågan måste besvaras ur appens egen data. Två signaler
 * väger tyngst och är dessutom fakta snarare än antaganden:
 *
 *  1. Registrerade löntagare på organisationen.
 *  2. Inlästa arbetsgivardeklarationer för organisationen.
 *
 * Saknas båda faller vi tillbaka på organisationsformen. En bostadsrätts- eller
 * ekonomisk förening sköts nästan alltid av en obetald styrelse och lämnar
 * ingen arbetsgivardeklaration alls — att påminna om en varje månad är ren
 * brusgenerering. Ett aktiebolag antas däremot ha anställda tills motsatsen
 * visar sig, eftersom en missad deklaration kostar förseningsavgift.
 *
 * Användaren kan alltid överstyra bedömningen i panelens inställningar.
 */

// ----------------------------------------------------------------------

/** Organisationsformer som normalt saknar anställda. */
const FORMER_UTAN_ANSTÄLLDA: ReadonlySet<string> = new Set([
  'BOSTADSRÄTTSFÖRENING',
  'EKONOMISK_FÖRENING',
  'IDEELL_FÖRENING',
  'PRIVATPERSON',
  // Innehavaren av en enskild firma är inte anställd i den och tar ut vinst, inte lön. Den som
  // faktiskt har personal får bedömningen omvänd av löntagarna i appen.
  'ENSKILD_FIRMA',
]);

/**
 * Verksamhetsinriktningar som är arbetsgivare oavsett juridisk form.
 *
 * Inriktningen väger tyngre än formen, och måste göra det: ett föräldrakooperativ är en ekonomisk
 * förening, och den formen står i listan ovan. Utan det här undantaget hade en förskola med tolv
 * anställda bedömts sakna anställda, och arbetsgivardeklarationen hade uteblivit ur att
 * göra-listan varje månad.
 */
const INRIKTNINGAR_MED_ANSTÄLLDA: ReadonlySet<string> = new Set([
  'FÖRÄLDRAKOOPERATIV_FÖRSKOLA',
]);

export type Arbetsgivarsignal = {
  organisationsTyp?: string;
  /** Verksamhetsinriktningen, när organisationen har en. Väger tyngre än formen. */
  verksamhetsinriktning?: string | null;
  /** Antal löntagare registrerade på organisationen. undefined = ännu inte hämtat. */
  antalLöntagare?: number;
  /** Om det finns inlästa arbetsgivardeklarationer. undefined = ännu inte hämtat. */
  harArbetsgivardeklarationer?: boolean;
};

export type Arbetsgivarbedömning = {
  ärArbetsgivare: boolean;
  /** Varför bedömningen blev som den blev — visas i inställningarna. */
  anledning: string;
  /** True när svaret kommer ur faktisk data, inte ur organisationsformen. */
  härleddUrData: boolean;
};

function formbeskrivning(organisationsTyp?: string): string {
  if (!organisationsTyp) return 'Organisationen';
  const nyckel = organisationsTyp.toUpperCase();
  if (nyckel === 'PRIVATPERSON') return 'Privatperson';
  return ORGANISATIONS_TYP_LABELS[nyckel as OrganisationsTyp] ?? organisationsTyp;
}

export function bedömArbetsgivare(signal: Arbetsgivarsignal): Arbetsgivarbedömning {
  if ((signal.antalLöntagare ?? 0) > 0) {
    return {
      ärArbetsgivare: true,
      anledning: `${signal.antalLöntagare} registrerade löntagare i organisationen.`,
      härleddUrData: true,
    };
  }

  if (signal.harArbetsgivardeklarationer) {
    return {
      ärArbetsgivare: true,
      anledning: 'Det finns inlästa arbetsgivardeklarationer för organisationen.',
      härleddUrData: true,
    };
  }

  const form = formbeskrivning(signal.organisationsTyp);

  // Inriktningen före formen: den säger något om verksamheten, formen bara om juridiken.
  if (INRIKTNINGAR_MED_ANSTÄLLDA.has((signal.verksamhetsinriktning ?? '').toUpperCase())) {
    return {
      ärArbetsgivare: true,
      anledning: 'Föräldrakooperativ förskola — verksamheten drivs med anställd personal.',
      härleddUrData: false,
    };
  }

  if (FORMER_UTAN_ANSTÄLLDA.has((signal.organisationsTyp ?? '').toUpperCase())) {
    return {
      ärArbetsgivare: false,
      anledning: `${form} utan registrerade löntagare — antas sakna anställda.`,
      härleddUrData: false,
    };
  }

  return {
    ärArbetsgivare: true,
    anledning: `${form} — antas ha anställda tills löner eller deklarationer visar annat.`,
    härleddUrData: false,
  };
}
